import React from 'react';
import useBatteryStatus from '@di-shared/features/diag/battery/useBatteryStatus';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import {ROUTE_PATHS} from '@di-shared/routes';
import {CreatedMaintenanceRecordRequest} from '@di-shared/api/di/__generated';
import {WebviewUtils} from '@shared/webviewUtils';
import {emitEventLog} from '@di-shared/features/analytics';
import useWindowFocusTracker from '@di-shared/features/common/useWindowFocusTracker';
import ImageSelector from '@di-shared/features/common/ImageSelector';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';

interface DiagBatteryLifespanProps {
  vin: string;
}

function DiagBatteryLifespan({vin}: DiagBatteryLifespanProps) {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const {aged, replaceDate, refetchBatteryStatusData, refetchExchangeRecordData} = useBatteryStatus(vin);
  const {enableWindowFocusTracker, disableWindowFocusTracker} = useWindowFocusTracker({
    isInitiallyEnabled: false,
    onFocus: () => {
      refetchBatteryStatusData();
      refetchExchangeRecordData();
      disableWindowFocusTracker();
    },
  });
  return (
    <div
      className="tab_content aging_diagnosis"
      id={`${t('T30076')}_tabpanel`}
      aria-labelledby={`${t('T30076')}_tab`}
    >
      <section className="section">
        <div className="section_heading type_main">
          <h2
            className="section_heading_title"
            style={{whiteSpace: 'pre-wrap'}}
          >
            {aged ? t('T30136') : t('T30137')}
          </h2>
          <p
            className="section_heading_desc"
            style={{whiteSpace: 'pre-wrap'}}
          >
            {aged ? t('T30138') : t('T30139')}
          </p>
        </div>
        <div className="section_visual visual_battery">
          {aged ? (
            <ImageSelector
              className="section_visual_img"
              alt="section_visual_img"
              kiaLightSrc={() => import('@di-shared/assets/img/visual/battery_warning_light.png')}
              kiaDarkSrc={() => import('@di-shared/assets/img/visual/battery_warning.png')}
              hyundaiLightSrc={() => import('@di-shared/assets/img/visual/battery_warning_light.png')}
              hyundaiDarkSrc={() => import('@di-shared/assets/img/visual/battery_warning.png')}
              genesisLightSrc={() => import('@di-shared/assets/img/visual/battery_warning_light.png')}
              genesisDarkSrc={() => import('@di-shared/assets/img/visual/battery_warning.png')}
            />
          ) : (
            <ImageSelector
              className="section_visual_img"
              alt="section_visual_img"
              kiaLightSrc={() => import('@di-shared/assets/img/visual/battery_good_light.png')}
              kiaDarkSrc={() => import('@di-shared/assets/img/visual/battery_good.png')}
              hyundaiLightSrc={() => import('@di-shared/assets/img/visual/hyundai/battery_good_light.png')}
              hyundaiDarkSrc={() => import('@di-shared/assets/img/visual/hyundai/battery_good.png')}
              genesisLightSrc={() => import('@di-shared/assets/img/visual/genesis/battery_good_light.png')}
              genesisDarkSrc={() => import('@di-shared/assets/img/visual/genesis/battery_good.png')}
            />
          )}
        </div>
        <div className="section_container">
          <div className="section_container_header">
            <h3 className="title_xxsmall">{t('T30140')}</h3>
            <span className="change_date">
              {replaceDate ? `${CldrDatetimeFormatter.format(replaceDate, 'yMMMd')} ${t('T30296')}` : t('T30364')}
            </span>
          </div>
          <button
            type="button"
            className="change_more_btn"
            onClick={() => {
              emitEventLog({
                event: 'consumables_exchange_update',
                form_step: 'cta click',
                click_text: t('T30027'),
                click_location: 'body',
              });
              enableWindowFocusTracker();
              WebviewUtils.openNewWebView({
                url:
                  window.location.origin +
                  ROUTE_PATHS.DIAG_EXCHANGE_REGISTER.getFullPath(vin, [CreatedMaintenanceRecordRequest.sestCode._18]) +
                  '&isAccessDI=1',
                isModal: true,
                isNavigationBar: false,
                isToolBar: false,
              });
            }}
          >
            <span className="ic ic_plus_20px"></span>
            <span className="blind">{t('T30027')}</span>
          </button>
        </div>
      </section>

      <div className="divider divider_thick"></div>
      <section className="section section_notice">
        <ul className="notice_list">
          <li className="notice_item">{t('T30141')}</li>
          <li className="notice_item">{t('T30142')}</li>
          <li className="notice_item">{t('T30143')}</li>
        </ul>
      </section>
    </div>
  );
}

export default DiagBatteryLifespan;

export function DiagBatteryLifespanLoading() {
  return (
    <div className="tab_content aging_diagnosis">
      <section className="section">
        <div className="section_heading type_main">
          <h2
            className="section_heading_title skeleton"
            style={{height: 60}}
          />
          <p
            className="section_heading_desc skeleton"
            style={{height: 18}}
          />
        </div>
        <div className="section_visual visual_battery">
          <div
            className="section_visual_img skeleton"
            style={{height: 142.5}}
          />
        </div>
        <div
          className="section_container skeleton"
          style={{height: 72}}
        ></div>
      </section>
      <div className="divider divider_thick"></div>
      <section className="section section_notice">
        <ul className="notice_list">
          <li
            className="notice_item skeleton"
            style={{height: 128}}
          />
        </ul>
      </section>
    </div>
  );
}
